<template>
  <div class="sidebar">
    <transition name="wc-fade">
      <div
        class="sidebar-backdrop"
        :style="sidebarBackdropStyle"
        @click="requestClose"
        v-if="isMenuOpen"
      ></div>
    </transition>
    <transition name="wc-slide">
      <div v-if="isMenuOpen" class="sidebar-panel" :style="sidebarPanelStyle">
        <div class="sidebar-content">
          <slot></slot>
        </div>
      </div>
    </transition>
  </div>
</template>
<script>
import emitter from '@/eventBus';
import { SIDE_MENU_EVENTS } from '@/constants/EventConstants';
import usePlatform from '@/modules/platform/composables/usePlatform';
import MobileSidebarMetricsMixin from '../../mixins/MobileSidebarMetricsMixin';

export default {
  mixins: [MobileSidebarMetricsMixin],
  setup() {
    const { platform } = usePlatform();
    return { platform };
  },
  data() {
    return {
      isMenuOpen: false,
      lockedScrollY: 0,
    };
  },
  computed: {
    sidebarBackdropStyle() {
      return this.mobileSidebarStyle;
    },
    sidebarPanelStyle() {
      return this.sidebarBackdropStyle;
    },
  },
  created() {
    this.handleOpen = () => this.open();
    this.handleClose = () => this.close();
    this.handleToggle = () => this.toggle();

    emitter.$on(SIDE_MENU_EVENTS.OPEN, this.handleOpen);
    emitter.$on(SIDE_MENU_EVENTS.CLOSE, this.handleClose);
    emitter.$on(SIDE_MENU_EVENTS.TOGGLE, this.handleToggle);
  },
  beforeUnmount() {
    emitter.$off(SIDE_MENU_EVENTS.OPEN, this.handleOpen);
    emitter.$off(SIDE_MENU_EVENTS.CLOSE, this.handleClose);
    emitter.$off(SIDE_MENU_EVENTS.TOGGLE, this.handleToggle);
    if (this.isMenuOpen) {
      this.unlockBodyScroll();
    }
  },
  /**
   * Methods to change the sidebar status
   */
  methods: {
    open() {
      this.updateMobileSidebarMetrics();
      this.isMenuOpen = true;
      this.lockBodyScroll();
    },
    close() {
      this.isMenuOpen = false;
      this.unlockBodyScroll();
    },
    toggle() {
      if (this.isMenuOpen) {
        this.close();
        return;
      }

      this.open();
    },
    requestClose() {
      emitter.$emit(SIDE_MENU_EVENTS.CLOSE);
    },
    /**
     * doesn't honor `overflow: hidden` on body for touch scrolling, so once the
     * menu's own scroll area hits its top/bottom boundary, the gesture chains to the page
     * behind it. Pinning body via position:fixed removes it from the scroll chain entirely.
     */
    lockBodyScroll() {
      this.lockedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.lockedScrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    },
    unlockBodyScroll() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, this.lockedScrollY);
    },
  },
  watch: {
    $route(to, from) {
      if (this.isMenuOpen && to !== from) {
        this.requestClose();
      }
    },
  },
};
</script>
<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-slide-enter-active,
.wc-slide-leave-active {
  transition: transform 0.3s ease;
}

.wc-slide-enter-from,
.wc-slide-leave-to {
  transform: translateX(-100%);
  transition: all 150ms ease-in 0s;
}

.wc-fade-enter-active,
.wc-fade-leave-active {
  transition: opacity 0.3s;
}

.wc-fade-enter-from,
.wc-fade-leave-to {
  opacity: 0;
}

.close-icon {
  position: absolute;
  right: 0.875rem;
  top: 0.875rem;
  color: var(--primary, $primary);
  z-index: 2;
}

.sidebar-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  cursor: pointer;
  z-index: map-get($zindex, 'modern-overlays');
}

.sidebar-panel {
  position: fixed;
  left: 0;
  top: 0;

  width: 100vw; /* default for small screens */
  height: 100vh;

  background-color: var(--light);
  z-index: map-get($zindex, 'modern-overlays');
  box-shadow: 0.25rem 0 0.25rem rgba(0, 0, 0, 0.15);

  display: inline-flex;
  flex-direction: column;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  /* Stop rubber-band scroll from chaining to the page behind the fixed overlay on iOS. */
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;
}

/* Apply fixed width when screen is larger */
@media (min-width: 28.76rem) {
  .sidebar-panel {
    width: 405px;
  }
}
</style>
